import { ButtonLink, Divider, RiArrowRightLine, Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { BenchmarkType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { Capsule, CapsuleContent, CapsuleIcon, CapsuleLabel, CapsuleTrend } from '@/components';
import { NCCardSectionHeader, printLatestDate, printNthYearDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePathSet } from '@/hooks/usePathSet';
import { useScreenSize } from '@/hooks/useScreenSize';
import { getBenchmarksFromFacts, useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { assertBenchmarkType } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';
import { printShortMonth } from '@/utils/formatting/date';

export type PrecipitationStatsTileProps = {
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  smTrendFact: PlotReportFactElement<number | null> | undefined;
  precipitationTrendFact: PlotReportFactElement<number | null> | undefined;
};

export const PrecipitationStatsTile = ({
  latestAnalysisFact,
  smTrendFact,
  precipitationTrendFact,
}: PrecipitationStatsTileProps) => {
  const isLargeScreen = useScreenSize() === 'large';

  return (
    <Stack className='gap-8 p-6 lg:justify-center lg:gap-4 lg:p-8 lg:pr-2' data-testid='precipitation-stats-tile'>
      <SoilMoisture soilMoistureLatestAnalysis={latestAnalysisFact} />

      {isLargeScreen && <Divider className='border-dashed' />}

      <Trends soilMoistureTrendChange={smTrendFact} precipitationTrendChange={precipitationTrendFact} />
    </Stack>
  );
};

export type WaterHistoricalStatsTileProps = {
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  oneYearUpliftFact: PlotReportFactElement<number | null> | undefined;
  oneYearPctChangeFact: PlotReportFactElement<number | null> | undefined;
  soilMoistureLatestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  soilMoistureTrendChangeFact: PlotReportFactElement<number | null> | undefined;
  precipitationTrendChangeFact: PlotReportFactElement<number | null> | undefined;
};

export const WaterHistoricalStatsTile = ({
  latestAnalysisFact,
  oneYearUpliftFact,
  oneYearPctChangeFact,
  soilMoistureLatestAnalysisFact,
  soilMoistureTrendChangeFact,
  precipitationTrendChangeFact,
}: WaterHistoricalStatsTileProps) => {
  const isLargeScreen = useScreenSize() === 'large';

  return (
    <Stack className='gap-8 p-6 lg:justify-center lg:gap-4 lg:p-8 lg:pr-2' data-testid='water-historical-stats-tile'>
      <WaterHoldingCapacity
        waterHoldingCapacityLatestAnalysis={latestAnalysisFact}
        whc1yrTrend={oneYearPctChangeFact}
        waterHoldingCapacity1yChange={oneYearUpliftFact}
      />

      {isLargeScreen && <Divider className='border-dashed' />}

      <SoilMoisture soilMoistureLatestAnalysis={soilMoistureLatestAnalysisFact} />

      {isLargeScreen && <Divider className='border-dashed' />}

      <Trends
        soilMoistureTrendChange={soilMoistureTrendChangeFact}
        precipitationTrendChange={precipitationTrendChangeFact}
      />
    </Stack>
  );
};

const WaterHoldingCapacity = ({
  waterHoldingCapacityLatestAnalysis,
  whc1yrTrend,
  waterHoldingCapacity1yChange,
}: {
  waterHoldingCapacityLatestAnalysis: PlotReportFactElement<number | null> | undefined;
  whc1yrTrend: PlotReportFactElement<number | null> | undefined;
  waterHoldingCapacity1yChange: PlotReportFactElement<number | null> | undefined;
}) => {
  const { t } = useTranslation();

  const latestAnalysisTranslation = t('shared.ncaDetail.details.labels.latestAnalysis');

  let whc1yrTrendDisplay = `${useDisplayNumber(whc1yrTrend?.value ?? 0, {
    signDisplay: 'always',
  })} %`;
  whc1yrTrendDisplay = !isNullish(whc1yrTrend?.value) ? whc1yrTrendDisplay : '-';

  const lastMeasuredDate = printLatestDate([waterHoldingCapacityLatestAnalysis?.measured_at]);

  const whc1yChangeInitialMeasuredAt = lastMeasuredDate;
  const whc1yChangePreviousMeasuredAt = printNthYearDate([waterHoldingCapacity1yChange?.measured_at], -1);

  return (
    <div className='flex flex-col gap-8 lg:gap-4'>
      <NCCardSectionHeader
        title={t('shared.ncaDetail.details.water.labels.waterHoldingCapacity')}
        supplementaryInfo={`${latestAnalysisTranslation} ${lastMeasuredDate}`}
        popover={{
          title: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.title'),
          body: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.body'),
        }}
      />
      <Stack direction='row' className='flex-wrap gap-16'>
        <NCFactCapsule
          label={latestAnalysisTranslation}
          fact={waterHoldingCapacityLatestAnalysis}
          trend={
            typeof whc1yrTrend?.value === 'number' && (
              <CapsuleTrend variant={whc1yrTrend.value < 0 ? 'negative' : 'positive'}>
                {whc1yrTrendDisplay}
              </CapsuleTrend>
            )
          }
          capsuleIconProps={{
            icon: getNCChangeIcon(waterHoldingCapacityLatestAnalysis),
            variant: getNCChangeIconVariant(waterHoldingCapacityLatestAnalysis),
          }}
          infoPopoverProps={{
            title: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacityLatestAnalysis.title'),
            body: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacityLatestAnalysis.body', {
              date: lastMeasuredDate,
            }),
          }}
        />
        {!isNullish(waterHoldingCapacity1yChange?.value) && (
          <NCFactCapsule
            label={t('shared.ncaDetail.details.labels.oneYearChange')}
            fact={waterHoldingCapacity1yChange}
            capsuleIconProps={{
              icon: getNCChangeIcon(waterHoldingCapacity1yChange),
              variant: getNCChangeIconVariant(waterHoldingCapacity1yChange),
            }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.water.explainers.oneYearChange.title'),
              body: t('shared.ncaDetail.details.water.explainers.oneYearChange.body', {
                date: whc1yChangeInitialMeasuredAt,
                previousDate: whc1yChangePreviousMeasuredAt,
              }),
              popoverTriggerProps: {
                'data-testid': 'whc-since-previous-analysis-tooltip',
              },
            }}
          />
        )}
      </Stack>
    </div>
  );
};

export const WaterHoldingCapacityExplantionBanner = ({
  benchmarkFact,
  oneYearPctChangeFact,
  latestAnalysisFact,
}: {
  oneYearPctChangeFact: PlotReportFactElement<number | null> | undefined;
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  benchmarkFact: {
    high: PlotReportFactElement<number | null> | undefined;
    moderate: PlotReportFactElement<number | null> | undefined;
    low: PlotReportFactElement<number | null> | undefined;
  };
}) => {
  const { t } = useTranslation();
  const pathSet = usePathSet();
  const pathname = useLocation();

  const plotGroupId = usePlotGroupId();

  const isLargeScreen = useScreenSize() === 'large';

  let whc1yrTrendDisplay = `${useDisplayNumber(oneYearPctChangeFact?.value ?? 0, {
    signDisplay: 'always',
  })} %`;
  whc1yrTrendDisplay = !isNullish(oneYearPctChangeFact?.value) ? whc1yrTrendDisplay : '-';

  const whcBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: benchmarkFact.high,
    moderateBenchmarkFact: benchmarkFact.moderate,
    lowBenchmarkFact: benchmarkFact.low,
  });

  const whcBenchmarkType = useBenchmarkForFact(whcBenchmarks, latestAnalysisFact?.value);
  assertBenchmarkType(whcBenchmarkType, 'WaterHoldingCapacityExplantionBanner');
  assertBenchmarkType(whcBenchmarkType, 'WaterHoldingCapacityExplantionBanner', BenchmarkType.unchanged);
  assertBenchmarkType(whcBenchmarkType, 'WaterHoldingCapacityExplantionBanner', BenchmarkType.stable);
  assertBenchmarkType(whcBenchmarkType, 'WaterHoldingCapacityExplantionBanner', BenchmarkType.alerted);
  assertBenchmarkType(whcBenchmarkType, 'WaterHoldingCapacityExplantionBanner', BenchmarkType.very_high);
  const displayExplanationBanner = typeof oneYearPctChangeFact?.value === 'number';

  return displayExplanationBanner && whcBenchmarkType ? (
    <Stack className='px-6 pb-6'>
      <Stack className='gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
        <BenchmarkPill benchmarkType={whcBenchmarkType} />
        <span className='typography-body1Bold'>
          {t('shared.ncaDetail.details.water.explainers.whcBenchmark.title')}
        </span>
        <Stack spacing={isLargeScreen ? 28 : 2} direction={isLargeScreen ? 'row' : 'col'}>
          <div>
            <Trans
              i18nKey={`shared.ncaDetail.details.water.explainers.whcBenchmark.${whcBenchmarkType}`}
              values={{
                trendPercent: whc1yrTrendDisplay,
              }}
              components={{
                p: <p className='typography-body1 text-text-secondary' />,
              }}
            />
          </div>
          {/* TODO: TEMP FIX - Remove 'false &&' when Imapct Projections is fixed */}
          {/* eslint-disable-next-line sonarjs/no-redundant-boolean */}
          {false && (
            <ButtonLink
              to={buildPath(pathSet.impactProjections, { queryParams: { plotGroupId } })}
              state={{
                previousPath: pathname,
              }}
              variant='outline'
              className='w-fit h-fit'
            >
              {t('shared.ncaDetail.details.water.explainers.whcBenchmark.buttonLabel')}
            </ButtonLink>
          )}
        </Stack>
      </Stack>
    </Stack>
  ) : (
    <></>
  );
};

const SoilMoisture = ({
  soilMoistureLatestAnalysis,
}: {
  soilMoistureLatestAnalysis: PlotReportFactElement<number | null> | undefined;
}) => {
  const { t } = useTranslation();

  const latestAnalysisTranslation = t('shared.ncaDetail.details.labels.latestAnalysis');

  const lastMeasuredDate = printLatestDate([soilMoistureLatestAnalysis?.measured_at]);

  return (
    <div className='flex flex-col gap-8 lg:gap-4'>
      <NCCardSectionHeader
        title={t('shared.ncaDetail.details.water.labels.soilMoisture')}
        supplementaryInfo={`${latestAnalysisTranslation} ${lastMeasuredDate}`}
        popover={{
          title: t('shared.ncaDetail.details.water.explainers.soilMoisture.title'),
          body: t('shared.ncaDetail.details.water.explainers.soilMoisture.body'),
        }}
      />
      <NCFactCapsule
        label={latestAnalysisTranslation}
        fact={soilMoistureLatestAnalysis}
        capsuleIconProps={{
          icon: getNCChangeIcon(soilMoistureLatestAnalysis),
          variant: getNCChangeIconVariant(soilMoistureLatestAnalysis),
        }}
        infoPopoverProps={{
          title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
          body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', {
            date: lastMeasuredDate,
          }),
        }}
      />
    </div>
  );
};

const Trends = ({
  soilMoistureTrendChange,
  precipitationTrendChange,
}: {
  soilMoistureTrendChange: PlotReportFactElement<number | null> | undefined;
  precipitationTrendChange: PlotReportFactElement<number | null> | undefined;
}) => {
  const { t } = useTranslation();

  const lastMeasuredDate = printLatestDate([
    soilMoistureTrendChange?.measured_at,
    precipitationTrendChange?.measured_at,
  ]);

  return (
    <div className='flex flex-col gap-8 lg:gap-4'>
      <NCCardSectionHeader
        title={t('shared.ncaDetail.details.water.labels.droughtRisk')}
        supplementaryInfo={`${t('shared.ncaDetail.details.labels.latestEstimates')} ${lastMeasuredDate}`}
        popover={{
          title: t('shared.ncaDetail.details.water.explainers.droughtRisk.title'),
          body: t('shared.ncaDetail.details.water.explainers.droughtRisk.body', {
            currentMonth: printShortMonth(Date.now()),
          }),
        }}
      />
      <Stack direction='row' className='flex-wrap justify-between gap-8'>
        <SoilMoistureChange soilMoistureTrendChange={soilMoistureTrendChange} />
        <PrecipitationChange precipitationTrendChange={precipitationTrendChange} />
      </Stack>
    </div>
  );
};

const SoilMoistureChange = ({
  soilMoistureTrendChange,
}: {
  soilMoistureTrendChange: PlotReportFactElement<number | null> | undefined;
}) => {
  const { t } = useTranslation();

  const label = t('shared.ncaDetail.details.water.labels.soilMoisture');

  const infoPopoverProps = {
    title: t('shared.ncaDetail.details.water.explainers.soilMoisturePercentageChange.title'),
    body: t('shared.ncaDetail.details.water.explainers.soilMoisturePercentageChange.body', {
      currentMonth: printShortMonth(Date.now()),
    }),
  };

  if (soilMoistureTrendChange?.value === 0) {
    return (
      <Capsule
        label={<CapsuleLabel infoPopoverProps={infoPopoverProps}>{label}</CapsuleLabel>}
        content={<CapsuleContent>{t('global.analysis.noSignificantTrend')}</CapsuleContent>}
        thumbnail={<CapsuleIcon icon={<RiArrowRightLine />} variant='default' />}
      />
    );
  }

  return (
    <NCFactCapsule
      label={label}
      fact={soilMoistureTrendChange}
      capsuleIconProps={{
        icon: getNCChangeIcon(soilMoistureTrendChange),
        variant: getNCChangeIconVariant(soilMoistureTrendChange),
      }}
      infoPopoverProps={infoPopoverProps}
    />
  );
};

const PrecipitationChange = ({
  precipitationTrendChange,
}: {
  precipitationTrendChange: PlotReportFactElement<number | null> | undefined;
}) => {
  const { t } = useTranslation();

  const label = t('shared.ncaDetail.details.water.labels.precipitation');

  const infoPopoverProps = {
    title: t('shared.ncaDetail.details.water.explainers.precipitationPercentageChange.title'),
    body: t('shared.ncaDetail.details.water.explainers.precipitationPercentageChange.body', {
      currentMonth: printShortMonth(Date.now()),
    }),
  };

  if (precipitationTrendChange?.value === 0) {
    return (
      <Capsule
        label={<CapsuleLabel infoPopoverProps={infoPopoverProps}>{label}</CapsuleLabel>}
        content={<CapsuleContent>{t('global.analysis.noSignificantTrend')}</CapsuleContent>}
        thumbnail={<CapsuleIcon icon={<RiArrowRightLine />} variant='default' />}
      />
    );
  }

  return (
    <NCFactCapsule
      label={label}
      fact={precipitationTrendChange}
      capsuleIconProps={{
        icon: getNCChangeIcon(precipitationTrendChange),
        variant: getNCChangeIconVariant(precipitationTrendChange),
      }}
      infoPopoverProps={infoPopoverProps}
    />
  );
};
